import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { mixpanelIdentify, mixpanelPeopleSet, mixpanelPeopleSetOnce, getRegisteredFirstTouch } from 'src/app/shared/utils/mixpanel.util';
import { getStoredAdParams } from 'src/app/shared/utils/gtm.util';
import { clearUserScopedStorage } from 'src/app/shared/utils/user-scoped-storage.util';
import { User, WagmiStore, WalletAccount } from '../models/auth.models';
import { WalletStateService } from './wallet-state.service';
import { CsrfTokenService } from './csrf-token.service';
import { AccountService } from './account.service';
import { followSignedInCommunity, forgetSignedInMember } from 'src/app/core/services/co-brand.service';

@Injectable({ providedIn: 'root' })
export class UserStateService {
  private readonly userSubject = new BehaviorSubject<User | null>(null);

  readonly regulated$ = this.walletState.regulated$;
  readonly account$ = this.walletState.wallet$;
  readonly wagmiConnected$ = this.walletState.wagmiConnected$;
  readonly wagmiStore$ = this.walletState.wagmiStore$;
  readonly user$ = this.userSubject;

  constructor(
    
    private readonly walletState: WalletStateService,
    private readonly csrfTokenService: CsrfTokenService,
    private readonly accountService: AccountService
  ) {}

  get regulated(): boolean {
    return this.walletState.getRegulated();
  }

  get user(): User | null {
    return this.userSubject.value;
  }

  get account(): WalletAccount | null {
    return this.walletState.getWallet();
  }

  setRegulated(value: boolean): void {
    this.walletState.setRegulated(value);
  }

  setUser(user: User | null): void {
    this.userSubject.next(user);
    if (!user?._id) {
      // signed out: a community page keeps its look, its calls stop carrying Community-Id
      forgetSignedInMember();
      return;
    }

    // signed in on a ?community_id= page: their own community (or RealWorld) is shown
    void followSignedInCommunity(user);

    // distinct_id = mongo ObjectId (NOT wallet_address — see .claude/rules/security.md).
    // wallet_address is passed through as a property so we can still filter/segment on it.
    mixpanelIdentify(user._id, user.wallet_address);

    // Populate $name / $email on the People profile — covers cookie-restored sessions
    // that bypass the fresh-signin-success branches in sign-in.component.
    // Prefer full `name` (regulated-sign-in API confirmed), then first_name, then last_name.
    const peopleProps: Record<string, unknown> = {};
    const displayName = user.name || user.first_name || user.last_name || null;
    if (displayName) peopleProps['$name'] = displayName;
    if (user.email) peopleProps['$email'] = user.email;

    // Attach last-touch acquisition channel to the People profile so user lists slice by source.
    // Short keys only — no landing_page/referrer (those carry token-bearing URLs risk).
    const ad = getStoredAdParams();
    if (ad['utm_source']) peopleProps['utm_source'] = ad['utm_source'];
    if (ad['utm_medium']) peopleProps['utm_medium'] = ad['utm_medium'];
    if (ad['utm_campaign']) peopleProps['utm_campaign'] = ad['utm_campaign'];
    if (ad['gclid']) peopleProps['gclid'] = ad['gclid'];

    if (Object.keys(peopleProps).length > 0) {
      mixpanelPeopleSet(peopleProps);
    }

    // First-touch channel — write-once so re-logins or landing-set values are not clobbered.
    // Backstop for direct-to-app users who never hit landing (landing's people.set_once would
    // have stamped this on a landing→app journey; this ensures app-only users are also covered).
    //
    // Source it from the REGISTERED first_* super-props, not from the ad store. The
    // super-props are written as one atomic per-visit set (see registerAcquisitionProps),
    // so the People profile now records the same visit the event stream records. Rebuilding
    // it here from getStoredAdParams() re-introduced the blending bug on the profile:
    // people.set_once is write-once per property too, so a later visit could fill in
    // whichever first_* keys the original visit lacked.
    const firstTouchProps = getRegisteredFirstTouch();
    if (Object.keys(firstTouchProps).length > 0) {
      mixpanelPeopleSetOnce(firstTouchProps);
    }
  }

  setAccount(account: WalletAccount | null): void {
    // Pre-auth wallet-connected state stays anonymous in Mixpanel.
    // mixpanelIdentify only fires once we have a real authenticated user._id (via setUser).
    this.walletState.setWallet(account);
  }

  setWagmiStore(store: WagmiStore | null): void {
    this.walletState.setWagmiStore(store);
  }

  setWagmiConnected(value: boolean): void {
    this.walletState.setWagmiConnected(value);
  }

  clearAuthState(preserveWalletConnection: boolean = true): void {
    this.userSubject.next(null);
    // Clear the CSRF token from every auth-reset path (logout, session-expiry
    // in auth.interceptor, ...) so a stale X-CSRF-Token is never re-attached.
    this.csrfTokenService.clear();
    // Universal logout chokepoint (manual logout + 401 session-expiry both reach
    // here): drop the cached KYC status so a different user logging in within the
    // same SPA session never inherits the previous user's regulated-gating status.
    this.accountService.clearKycStatusCache();
    // Same chokepoint, same reason: drop the browser-storage caches that hold the outgoing
    // user's personal data — today the deposit flow's contact prefill (phone number, dial
    // code, region, contact window) and its uploaded-photo mirror. Left behind, the next
    // user to sign in on this browser would open the deposit form pre-filled with the
    // previous user's contact details.
    clearUserScopedStorage();
    // When logging out without an explicit wallet disconnect, leave the live
    // Wagmi-driven wallet state intact so the user can re-sign-in without
    // reconnecting. AppkitService remains subscribed to watchAccount and will
    // continue to drive WalletStateService.
    if (!preserveWalletConnection) {
      this.walletState.clearWalletState();
    }
  }
}
